import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { BTLocalStorage } from "types/btStorage";

import { isInPortal } from "utilities/portal/portal";

import { JobPickerDisplayModes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { useDraftChangeListener } from "entity/schedule/ScheduleDrafts/draftChangeChannel";
import { getAllDraftsForBuilder } from "entity/schedule/ScheduleDrafts/useDraftStorage";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";

/**
 * Job ids that currently have a schedule draft, for badging in the job picker.
 *
 * Kept apart from useDraftManager: the job picker mounts on every list page, and this only needs a
 * storage read plus the change channel, not the gantt and validation machinery draft editing uses.
 */
export function useScheduledDraftsForJobPicker(
    builderId: number,
    displayMode: JobPickerDisplayModes,
) {
    const location = useLocation();
    const [draftJobIds, setDraftJobIds] = useState<number[]>([]);
    const [isDraftModeEnabled, setIsDraftModeEnabled] = useState(
        BTLocalStorage.get("bt-boolean-useDraftMode") ?? false,
    );

    const shouldDisplayDrafts = useMemo(() => {
        const isOnGanttTab = location.pathname.includes(
            `/Schedules/${ScheduleListContainerTabs.Gantt}`,
        );

        return (
            isInPortal({ builder: true }) &&
            displayMode === JobPickerDisplayModes.Calendar &&
            isOnGanttTab &&
            isDraftModeEnabled
        );
    }, [displayMode, isDraftModeEnabled, location.pathname]);

    const updateDraftJobIds = useCallback(async () => {
        if (shouldDisplayDrafts) {
            const drafts = await getAllDraftsForBuilder(builderId);
            const jobIds = drafts
                .map((draft) => draft.jobId)
                .filter((id): id is number => id !== undefined);
            setDraftJobIds(jobIds);
        } else {
            setDraftJobIds([]);
        }
    }, [shouldDisplayDrafts, builderId]);

    // Listen for changes to draft mode setting
    useEffect(() => {
        const handleDraftModeChange = (oldValue: boolean | null, newValue: boolean | null) => {
            setIsDraftModeEnabled(newValue ?? false);
        };

        BTLocalStorage.addChangeListener("bt-boolean-useDraftMode", handleDraftModeChange);

        return () => {
            BTLocalStorage.removeChangeListener("bt-boolean-useDraftMode", handleDraftModeChange);
        };
    }, []);

    useDraftChangeListener(updateDraftJobIds);

    useEffect(() => {
        void updateDraftJobIds();
    }, [updateDraftJobIds]);

    return draftJobIds;
}
